import React from 'react';
import { Users, Info, X } from 'lucide-react';
import { interests } from '../data/interests';
import { cities } from '../data/cities';

const LandingPage = ({ 
  signupForm, 
  setSignupForm, 
  handleSignup, 
  toggleInterest,
  setCurrentView,
  showTestInfo,
  setShowTestInfo 
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50">
      {/* Test Info Banner */}
      {showTestInfo && (
        <div className="bg-blue-600 text-white px-4 py-3">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Info className="w-5 h-5" />
              <span className="text-sm">
                <strong>Testing Mode:</strong> Use the "Log In" button and try: test@example.com / test123
              </span>
            </div>
            <button
              onClick={() => setShowTestInfo(false)}
              className="text-white hover:text-blue-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-8">
        <nav className="flex justify-between items-center mb-16">
          <div className="flex items-center space-x-2">
            <Users className="w-8 h-8 text-red-600" />
            <span className="text-2xl font-bold text-gray-800">SwissMeet</span>
          </div>
          <button
            onClick={() => setCurrentView('login')}
            className="px-6 py-2 text-red-600 border-2 border-red-600 rounded-full hover:bg-red-600 hover:text-white transition-all duration-300"
          >
            Log In
          </button>
        </nav>

        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Swiss<span className="text-red-600">Meet</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Stop wondering what to do on Saturday afternoons
            </p>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Meet new people in Switzerland who share your interests. 
              Because making friends shouldn't be this hard.
            </p>
          </div>

          {/* Signup Form */}
          <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl p-8 md:p-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Join the Community</h2>
            
            <form onSubmit={handleSignup} className="space-y-6">
              {/* Name & Email Row */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    required
                    value={signupForm.name}
                    onChange={(e) => setSignupForm(prev => ({...prev, name: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={signupForm.email}
                    onChange={(e) => setSignupForm(prev => ({...prev, email: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    placeholder="your@email.com"
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  required
                  value={signupForm.password}
                  onChange={(e) => setSignupForm(prev => ({...prev, password: e.target.value}))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                  placeholder="Create a password"
                  autoComplete="new-password"
                />
              </div>

              {/* City & Gender Preference */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <select
                    required
                    value={signupForm.city}
                    onChange={(e) => setSignupForm(prev => ({...prev, city: e.target.value, location: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                  >
                    <option value="">Select city</option>
                    {cities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender Preference</label>
                  <select
                    value={signupForm.genderPreference}
                    onChange={(e) => setSignupForm(prev => ({...prev, genderPreference: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                  >
                    <option value="all">Everyone</option>
                    <option value="women">Women only</option>
                    <option value="men">Men only</option>
                  </select>
                </div>
              </div>

              {/* Interests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Interests</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {interests.map(interest => {
                    const Icon = interest.icon;
                    const isSelected = signupForm.interests.includes(interest.id);
                    return (
                      <button
                        key={interest.id}
                        type="button"
                        onClick={() => toggleInterest(interest.id)}
                        className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-red-500 bg-red-50 text-red-600'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{interest.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Social Links */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instagram (optional)</label>
                  <input
                    type="text"
                    value={signupForm.socials.instagram}
                    onChange={(e) => setSignupForm(prev => ({...prev, socials: {...prev.socials, instagram: e.target.value}}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    placeholder="@username"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn (optional)</label>
                  <input
                    type="text"
                    value={signupForm.socials.linkedin}
                    onChange={(e) => setSignupForm(prev => ({...prev, socials: {...prev.socials, linkedin: e.target.value}}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    placeholder="linkedin.com/in/..."
                    autoComplete="off"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-xl hover:from-red-600 hover:to-red-700 transform hover:scale-[1.02] transition-all duration-300 shadow-lg"
              >
                Sign Up & Start Meeting People
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
